import type { Price } from "@brookbush/types";

import clsx from "clsx";

import BecomeAMemberJoiningPlanCopy from "./BecomeAMemberJoiningPlanCopy";
import { useAccess, useStripeSubscription } from "~/hooks";
import useCheckout from "~/hooks/useCheckout";
import useJoiningPrice from "~/hooks/useJoiningPrice";
import usePayPalSubscription from "~/hooks/usePayPalSubscription";
import usePlanConfig from "~/hooks/usePlanConfig";
import useStripePrices from "~/hooks/useStripePrices";
import captureBecomeAMemberEvent from "~/lib/captureBecomeAMemberEvent";
import { getSelectedPlanJoiningCopy } from "~/lib/membership-joining-price";
import {
	getAnnualMonthlyEquivalent,
	getAnnualSavingsVsMonthly,
} from "~/lib/membership-plan-defaults";
import { shouldLockPlanSelection } from "~/lib/stripe/checkoutPaymentGuards";
import {
	convertToDollars,
	formatPrice,
	getAmountIndividual,
	getAmountIndividualAfterDiscount,
	getBillingInterval,
	isSamePlan,
} from "~/lib/stripe/helpers";

type Props = {};
const BecomeAMemberModalPlanSelector = ({}: Props) => {
	const { planConfig, setPrice } = usePlanConfig();
	const { prices } = useStripePrices(planConfig.planType);
	const { price: subscriptionPrice } = useStripeSubscription();
	const { paypalSubscription } = usePayPalSubscription();
	const { isMember } = useAccess();
	const { isPaymentSubmitting } = useCheckout();
	const { offer: joiningOffer, ready: isJoiningPriceReady } = useJoiningPrice();
	const isPlanLocked = shouldLockPlanSelection(isPaymentSubmitting);

	const yearlyPrice = prices?.find(
		(price) => price.recurring?.interval === "year",
	);

	const monthlyPrice = prices?.find(
		(price) => price.recurring?.interval === "month",
	);

	const yearlySavings = getAnnualSavingsVsMonthly(
		monthlyPrice ? getAmountIndividual(monthlyPrice) : 0,
		yearlyPrice ? getAmountIndividual(yearlyPrice) : 0,
	);

	const yearlyMonthlyEquivalent = yearlyPrice
		? getAnnualMonthlyEquivalent(getAmountIndividual(yearlyPrice))
		: 0;

	const cardCopy = (price: Price) =>
		isMember
			? null
			: getSelectedPlanJoiningCopy({
					planConfig: { ...planConfig, price },
					prices,
					offer: joiningOffer,
				});

	const isActivePrice = (price: Price) =>
		isMember &&
		(subscriptionPrice
			? isSamePlan(price, subscriptionPrice)
			: price.recurring?.interval ===
				paypalSubscription?.plan.billing_cycles[0].frequency.interval_unit.toLowerCase());

	return (
		<div className="flex w-full flex-col gap-3">
			{prices?.map((price) => {
				const isYearly = price.recurring?.interval === "year";
				const isSelected = planConfig.price?.id === price.id;
				const showPromo = !!planConfig.promoCode && !isActivePrice(price);

				return (
					<button
						disabled={isActivePrice(price) || isPlanLocked}
						onClick={(_e) => {
							if (isPlanLocked) return;
							captureBecomeAMemberEvent("plan_selected", {
								surface: "modal",
								interval: price.recurring?.interval,
								promo_code: planConfig.promoCode?.code,
								price_id: price.id,
								amount_due: getAmountIndividual(price),
							});
							setPrice(price);
						}}
						key={price.id}
						className={clsx(
							"flex w-full flex-col items-start gap-1 rounded-xl border-2 px-3 py-2.5 text-left hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-slate-700",
							{
								"!border-emerald-400 shadow-slate-100 shadow-xl dark:shadow-slate-900/50":
									isSelected,
							},
						)}
					>
						<div className="flex w-full items-center justify-between gap-2">
							<span className="font-medium capitalize">
								{getBillingInterval(price)}ly
							</span>
							{isYearly && (
								<span className="rounded-md bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700 text-xs dark:bg-emerald-950/50 dark:text-emerald-300">
									Recommended
								</span>
							)}
							{!isYearly && showPromo && (
								<span className="font-semibold text-emerald-600 text-xs dark:text-emerald-300">
									{planConfig.promoCode!.coupon.percent_off
										? `${planConfig.promoCode!.coupon.percent_off}% off`
										: `Save $${convertToDollars(
												planConfig.promoCode!.coupon.amount_off!,
											)}`}
								</span>
							)}
						</div>

						{cardCopy(price) ? (
							<BecomeAMemberJoiningPlanCopy
								copy={cardCopy(price)!}
								isReady={isJoiningPriceReady}
								isYearly={isYearly}
							/>
						) : showPromo ? (
							<span className="flex shrink-0 flex-wrap items-baseline gap-x-2 whitespace-nowrap tabular-nums">
								<span className="font-semibold text-emerald-600 dark:text-emerald-300">
									{formatPrice({
										currency: planConfig.currency,
										value: getAmountIndividualAfterDiscount(
											price,
											planConfig.promoCode!.coupon,
										),
									})}
								</span>
								<span className="text-slate-500 line-through dark:text-slate-400">
									{formatPrice({
										currency: planConfig.currency,
										value: getAmountIndividual(price),
									})}
								</span>
							</span>
						) : (
							<span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">
								{formatPrice({
									currency: planConfig.currency,
									value: getAmountIndividual(price),
								})}
							</span>
						)}

						{cardCopy(price) ? null : isYearly ? (
							<div className="flex flex-col gap-0.5 text-emerald-600 text-xs leading-snug dark:text-emerald-300">
								{yearlyMonthlyEquivalent > 0 && (
									<span>
										{formatPrice({
											currency: planConfig.currency,
											value: yearlyMonthlyEquivalent,
										})}
										/mo equivalent, billed yearly
									</span>
								)}
								<span>2 months free</span>
								{yearlySavings > 0 && (
									<span>
										Save{" "}
										{formatPrice({
											currency: planConfig.currency,
											value: yearlySavings,
										})}
									</span>
								)}
								{showPromo && (
									<span>
										{planConfig.promoCode!.coupon.percent_off
											? `+${planConfig.promoCode!.coupon.percent_off}% off`
											: `+$${convertToDollars(
													planConfig.promoCode!.coupon.amount_off!,
												)}`}
									</span>
								)}
							</div>
						) : (
							<span className="text-slate-500 text-xs dark:text-slate-400">
								Billed monthly
							</span>
						)}
					</button>
				);
			})}
		</div>
	);
};

export default BecomeAMemberModalPlanSelector;
